import React, { useState, useEffect } from "react";
import { ScrollShadow } from "@heroui/react";
import { ChatMessage } from "@webuild-ai/admin/models/components";
import { useTranslation } from "react-i18next";
import { useUser } from "@webuild-ai/react-auth";
import { useBrand } from "@webuild-ai/react-ui/hooks";

import { brandIconSrc } from "@/config/tenants";

import { useChatStore, ExtendedChatMessage } from "../../stores/ChatStore";

import { MessageCard } from "./MessageCard";
import { SystemMessage } from "./SystemMessage";
import { SavePromptModal } from "./SavePromptModal";

const THINKING_PHRASES = [
  "Thinking it over",
  "Give me a few",
  "Researching & reasoning",
  "Working on it",
  "Processing your request",
  "Let me think",
  "Analysing",
  "On it",
  "Just a moment",
  "Gathering insights",
];

interface ChatPanelProps {
  runId: string;
  messages: (ChatMessage | ExtendedChatMessage)[];
}

const ChatPanel = ({ runId, messages }: ChatPanelProps) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { userProfile } = useUser();
  const userName = userProfile?.name;
  const userIcon = userProfile?.picture
    ? userProfile?.picture
    : !userName
      ? "https://d2u8k2ocievbld.cloudfront.net/memojis/male/6.png"
      : "";
  const [isSavePromptModalOpen, setIsSavePromptModalOpen] = useState(false);
  const [promptToSave, setPromptToSave] = useState("");
  const { streaming } = useChatStore();
  const { brand } = useBrand();

  const [shouldAutoScroll, setShouldAutoScroll] = React.useState(true);
  const [thinkingText, setThinkingText] = useState("");

  // Pick a random thinking phrase when streaming starts
  useEffect(() => {
    if (streaming) {
      const randomPhrase = THINKING_PHRASES[Math.floor(Math.random() * THINKING_PHRASES.length)];

      setThinkingText(randomPhrase);
    }
  }, [streaming]);

  const BOTTOM_THRESHOLD_PX = 80;

  const handleScroll = React.useCallback(() => {
    const el = scrollRef.current;

    if (!el) return;
    const isNearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - BOTTOM_THRESHOLD_PX;

    setShouldAutoScroll(isNearBottom);
  }, []);

  React.useEffect(() => {
    const el = scrollRef.current;

    if (!el) return;
    if (shouldAutoScroll) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, shouldAutoScroll]);

  const onSavePrompt = (prompt: string) => {
    setPromptToSave(prompt);
    setIsSavePromptModalOpen(true);
  };

  return (
    <div className="h-full flex flex-col">
      <SavePromptModal
        isOpen={isSavePromptModalOpen}
        promptToSave={promptToSave}
        onOpenChange={setIsSavePromptModalOpen}
        onSuccess={() => {
          // Optionally refresh any data if needed
          setIsSavePromptModalOpen(false);
        }}
      />
      <ScrollShadow
        ref={scrollRef}
        className="flex-1 flex flex-col gap-4 overflow-y-auto pb-8 px-2 py-2 sm:px-3 sm:py-4"
        onScroll={handleScroll}
      >
        {messages.map((message, index) => {
          const {
            role,
            content,
            status,
            agent,
            id,
            liked,
            toolCallExecutions,
            usage,
            timestamp,
            files,
            ...rest
          } = message;

          // Check for system message type (with backward compatibility)
          const messageType = (message as ExtendedChatMessage).messageType || "assistant";
          const isSystemMessage = messageType === "system";

          // Render system messages with simplified styling
          if (isSystemMessage) {
            const contentStr =
              typeof content === "string"
                ? content
                : (content as any)?.prompt || (content as any)?.content || "";

            return <SystemMessage key={index} content={contentStr} timestamp={timestamp} />;
          }

          // Render normal user/assistant messages
          return (
            <MessageCard
              key={index}
              agent={agent || ""}
              avatar={role === "user" ? userIcon : brandIconSrc(brand.folder, "pathfinder.svg")}
              citations={(rest as any).citations}
              dataId={`card-${index}`}
              liked={liked}
              message={(function () {
                const topFiles = Array.isArray(files) ? files : [];
                const contentObj = content && typeof content === "object" ? (content as any) : null;
                const embeddedFiles =
                  contentObj && Array.isArray(contentObj.files)
                    ? contentObj.files
                    : contentObj && Array.isArray(contentObj.attachments)
                      ? contentObj.attachments
                      : [];
                const allFiles = topFiles.length > 0 ? topFiles : embeddedFiles;
                const promptVal =
                  typeof content === "string"
                    ? content
                    : (contentObj?.prompt ?? contentObj?.content ?? "");
                const msgProp =
                  allFiles.length > 0 ? ({ prompt: promptVal, files: allFiles } as any) : content;

                return msgProp;
              })()}
              messageClassName={role === "user" ? "bg-content3 text-content3-foreground" : ""}
              messageID={id}
              runId={runId}
              showFeedback={role === "assistant"}
              status={status as "success" | "failed" | "streaming" | "stopped"}
              timestamp={timestamp}
              toolCallExecutions={toolCallExecutions || []}
              usage={usage || undefined}
              userName={userName || ""}
              onSavePrompt={onSavePrompt}
            />
          );
        })}
        {streaming && (
          <div data-id="chat-panel-thinking">
            <div className="flex items-center gap-2 p-4 ml-10 sm:ml-[52px]">
              <span className="text-sm text-default-500 dark:text-default-400 animate-pulse">
                {t(thinkingText)}...
              </span>
            </div>
          </div>
        )}
      </ScrollShadow>
    </div>
  );
};

const MemoizedChatPanel = React.memo(ChatPanel);

export { MemoizedChatPanel as ChatPanel };
